import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Image,
  SimpleGrid,
  useDisclosure,
  Divider
} from '@chakra-ui/react';
import { FaCheck, FaWhatsapp, FaPhone, FaCrown, FaImage } from 'react-icons/fa';
import { FiArrowRight } from 'react-icons/fi';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import RegistrationModal from '../shared/RegistrationModal';
import axiosInstance from '../services/axiosInstance';
import CobraSpinner from '../shared/CobraSpinner';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const PackDetails = () => {
  const { packId } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Theme Constants
  const brandYellow = '#F2C919';
  const lightBg = 'gray.50';
  const primaryText = '#2C3E50';

  useEffect(() => {
    const fetchPack = async () => {
      try {
        const { data } = await axiosInstance.get(`/packs/${packId}`);
        setPack(data);
      } catch (error) {
        console.error("Error fetching pack details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPack();
  }, [packId]);

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/21622779137`, '_blank');
  };

  const handlePhoneClick = () => {
    window.open(`tel:+21622779137`, '_blank');
  };

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl" bg={lightBg}>
        <VStack spacing={6}>
          <CobraSpinner size="xl" color={brandYellow} thickness="4px" />
          <Text color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="lg">
            جاري تحميل تفاصيل الباقة...
          </Text>
        </VStack>
      </Flex>
    );
  }

  if (!pack) {
    return (
      <Box dir="rtl" bg={lightBg} minH="100vh">
        <Header />
        <Container maxW="1200px" py={32} textAlign="center">
          <Heading color="red.500" fontFamily="Tajawal, sans-serif">الباقة غير موجودة</Heading>
          <Button mt={8} onClick={() => navigate(-1)} size="lg" bg={primaryText} color="white">العودة</Button>
        </Container>
      </Box>
    );
  }

  const imageUrl = pack.imageUrl ? (pack.imageUrl.startsWith('http') ? pack.imageUrl : `${axiosInstance.defaults.baseURL.replace('/api', '')}${pack.imageUrl}`) : null;

  return (
    <Box dir="rtl" bg={lightBg} minH="100vh" overflowX="hidden">
      <Header />
      
      <Box pt={{ base: 32, md: 40 }} pb={{ base: 8, md: 12 }}>
        <Container maxW="1400px">
          <Button
            leftIcon={<FiArrowRight />}
            variant="link"
            color="gray.500"
            _hover={{ color: brandYellow }}
            mb={6}
            onClick={() => navigate(-1)}
            fontFamily="Tajawal, sans-serif"
            fontSize="lg"
          >
            العودة
          </Button>
        </Container>
      </Box>

      <Container maxW="1400px" pb={24}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 12, lg: 20 }} alignItems="start">
          {/* Image Column */}
          <MotionBox
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              bg="white"
              borderRadius="3xl"
              overflow="hidden"
              boxShadow="0 30px 60px rgba(0,0,0,0.1)"
              position="relative"
              border="1px solid"
              borderColor="gray.100"
            >
              {pack.isPopular && (
                <Badge
                  position="absolute"
                  top={6}
                  right={6}
                  bg={brandYellow}
                  color="black"
                  px={6}
                  py={2}
                  borderRadius="full"
                  fontWeight="900"
                  fontSize="md"
                  fontFamily="Tajawal, sans-serif"
                  boxShadow="lg"
                  zIndex={2}
                >
                  الأكثر طلباً
                </Badge>
              )}
              {imageUrl ? (
                <Image src={imageUrl} alt={pack.nameAr || pack.name} w="100%" h="auto" objectFit="cover" />
              ) : (
                <VStack spacing={4} py={32} textAlign="center" bg="gray.100" w="100%">
                  <Icon as={FaImage} boxSize={20} color="gray.300" />
                  <Heading size="md" color={primaryText} fontFamily="Tajawal, sans-serif">{pack.nameAr || pack.name}</Heading>
                </VStack>
              )}
            </Box>
          </MotionBox>

          {/* Details Column */}
          <MotionBox
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box bg="white" p={{ base: 8, md: 10 }} borderRadius="3xl" boxShadow="0 20px 40px rgba(0,0,0,0.05)" border="1px solid" borderColor="gray.100">
              <VStack align="stretch" spacing={8}>
                <VStack align="start" spacing={4}>
                  {pack.niveau && (
                    <Badge bg={`${brandYellow}20`} color={primaryText} px={4} py={2} borderRadius="full" fontSize="sm" fontWeight="800" fontFamily="Tajawal, sans-serif">
                      {pack.niveau.name}
                    </Badge>
                  )}
                  <Heading size="2xl" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="900" lineHeight="1.3">
                    {pack.nameAr || pack.name}
                  </Heading>
                  {pack.description && (
                    <Text fontSize="xl" color="gray.600" fontFamily="Tajawal, sans-serif" lineHeight="1.8">
                      {pack.description}
                    </Text>
                  )}
                </VStack>

                <Divider borderColor="gray.100" />

                <Flex justify="space-between" align="flex-end" wrap="wrap" gap={6}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="md" color="gray.500" fontFamily="Tajawal, sans-serif" fontWeight="700" mb={1}>
                      السعر
                    </Text>
                    {pack.originalPrice && (
                      <Text
                        fontSize="xl"
                        color="red.400"
                        textDecoration="line-through"
                        fontFamily="Tajawal, sans-serif"
                        fontWeight="700"
                      >
                        {pack.originalPrice} {pack.currency || 'TND'}
                      </Text>
                    )}
                    <HStack align="baseline" spacing={3}>
                      <Text fontSize="6xl" fontWeight="900" color={primaryText} fontFamily="Tajawal, sans-serif" lineHeight="1">
                        {pack.price}
                      </Text>
                      <Text fontSize="2xl" color={brandYellow} fontFamily="Tajawal, sans-serif" fontWeight="900">
                        {pack.currency || 'TND'}
                      </Text>
                    </HStack>
                  </VStack>
                  
                  <Button
                    size="xl"
                    h="70px"
                    px={10}
                    bg={primaryText}
                    color="white"
                    _hover={{ bg: 'black', transform: 'scale(1.02)' }}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="900"
                    fontSize="xl"
                    onClick={onOpen}
                    borderRadius="2xl"
                    boxShadow="xl"
                    transition="all 0.3s"
                    w={{ base: '100%', md: 'auto' }}
                  >
                    تسجيل أولي
                  </Button>
                </Flex>

                {(pack.features?.length > 0 || pack.includedCourses?.length > 0) && (
                   <Box pt={4}>
                    <Heading size="md" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="900" mb={4}>
                      محتويات الباقة
                    </Heading>
                    
                    {pack.features && pack.features.length > 0 && (
                      <List spacing={3} mb={pack.includedCourses?.length > 0 ? 4 : 0}>
                        {pack.features.map((feature, index) => (
                          <ListItem key={index}>
                            <HStack spacing={3} align="flex-start">
                              <Icon as={FaCheck} color={brandYellow} boxSize={4} mt={1} />
                              <Text fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="600" color="gray.700">
                                {feature}
                              </Text>
                            </HStack>
                          </ListItem>
                        ))}
                      </List>
                    )}
                    
                    {pack.includedCourses && pack.includedCourses.length > 0 && (
                      <>
                        {pack.features?.length > 0 && <Divider borderColor="gray.100" my={4} />}
                        <Text fontSize="md" fontWeight="800" color={primaryText} fontFamily="Tajawal, sans-serif" mb={3}>
                          المواد المضمنة:
                        </Text>
                        <List spacing={3}>
                          {pack.includedCourses.map((course, index) => (
                            <ListItem key={index}>
                              <HStack spacing={3} align="flex-start">
                                <Icon as={FaCheck} color={brandYellow} boxSize={4} mt={1} />
                                <Text fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="600" color="gray.700">
                                  {course.title || course.name}
                                </Text>
                              </HStack>
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </Box>
                )}

                <Divider borderColor="gray.100" />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  <Button
                    size="lg"
                    h="60px"
                    bg="#25D366"
                    color="white"
                    _hover={{ bg: '#20bd5a', transform: 'translateY(-2px)' }}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="800"
                    onClick={handleWhatsAppClick}
                    borderRadius="xl"
                    leftIcon={<FaWhatsapp size={20} />}
                    transition="all 0.3s"
                  >
                    واتساب
                  </Button>
                  <Button
                    size="lg"
                    h="60px"
                    variant="outline"
                    borderColor="gray.200"
                    color={primaryText}
                    _hover={{ bg: 'gray.50', borderColor: 'gray.300', transform: 'translateY(-2px)' }}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="800"
                    onClick={handlePhoneClick}
                    borderRadius="xl"
                    leftIcon={<FaPhone />}
                    transition="all 0.3s"
                  >
                    استفسار
                  </Button>
                </SimpleGrid>
              </VStack>
            </Box>
          </MotionBox>
        </SimpleGrid>
      </Container>
      <Footer />

      {isOpen && (
        <RegistrationModal
          isOpen={isOpen}
          onClose={onClose}
          niveau={pack.niveau}
          pack={pack}
        />
      )}
    </Box>
  );
};

export default PackDetails;
